import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

import { IFormOption } from '../_core/_ui/forms.component';
import { Sport } from '../../lib/types';

// Reference data a stage can reference (drills / practice plans / positions), shared by the
// assessment stage builder and the format-template preview so both resolve options identically.
export const GET_STAGE_REFERENCE_DATA = gql`
  query GetStageReferenceData($companyId: ID!, $includeGlobal: Boolean, $includeGlobalForSport: SPORT) {
    practicePlans(companyId: $companyId, includeGlobal: $includeGlobal) {
      _id
      name
      companyId
    }
    drills(companyId: $companyId, includeGlobal: $includeGlobal) {
      _id
      name
      companyId
    }
    positions(companyId: $companyId, includeGlobalForSport: $includeGlobalForSport) {
      _id
      name
    }
  }
`;

interface IUseStageReferenceDataParams {
  companyId?: string;
  // Positions load scoped to this sport; pass the template's sport for sport-scoped templates,
  // otherwise the active company's sport.
  sport: Sport;
  includeGlobal?: boolean;
  // Drop company-owned drills/plans from the options. Positions are unaffected — they are
  // already global reference data (see "Positions are global and sport-scoped" in CLAUDE.md).
  globalOnly?: boolean;
  skip?: boolean;
}

interface IUseStageReferenceDataResult {
  drillOptions: IFormOption[];
  practicePlanOptions: IFormOption[];
  positionOptions: IFormOption[];
  loading: boolean;
}

interface IReferenceItem { _id: string; name: string; companyId?: string | null }

const toOptions = (items: IReferenceItem[]): IFormOption[] =>
  items.map(item => ({ value: item._id, label: item.name }));

// A company-owned id stored on a SYSTEM template resolves for no other association, so those
// options are withheld rather than offered. See "Format-template SESSION stages embed
// company-scoped drill/practicePlan IDs" in CLAUDE.md.
const selectGlobal = (items: IReferenceItem[], globalOnly?: boolean): IReferenceItem[] =>
  (globalOnly ? items.filter(item => !item.companyId) : items);

export const useStageReferenceData = ({
  companyId,
  sport,
  includeGlobal,
  globalOnly,
  skip,
}: IUseStageReferenceDataParams): IUseStageReferenceDataResult => {
  const { data, loading } = useQuery(GET_STAGE_REFERENCE_DATA, {
    skip: Boolean(skip) || !companyId,
    // globalOnly still needs the globals in the response, so it forces the union on.
    variables: { companyId, includeGlobal: globalOnly || includeGlobal, includeGlobalForSport: sport },
    fetchPolicy: 'cache-and-network',
  });

  const drillOptions = useMemo(
    () => toOptions(selectGlobal((data && data.drills) || [], globalOnly)),
    [data, globalOnly],
  );
  const practicePlanOptions = useMemo(
    () => toOptions(selectGlobal((data && data.practicePlans) || [], globalOnly)),
    [data, globalOnly],
  );
  const positionOptions = useMemo(() => toOptions((data && data.positions) || []), [data]);

  return { drillOptions, practicePlanOptions, positionOptions, loading };
};
